// Jarvan IV E+Q combo VFX:
//   1. Demacian Standard shoots to destination and plants
//   2. Lance beam fires along the dash path
//   3. Jarvan dashes to the standard
//   4. Impact shockwave at landing

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";
import type { UnitVisual } from "../UnitVisual";

const STD_TRAVEL_MS = 180; // standard projectile flies to destination
const STD_PLANT_MS  = 140; // standard is planted, Jarvan winds up
const DASH_MS       = 280; // Jarvan slides to standard
const IMPACT_MS     = 300; // shockwave at landing

const DASH_START   = STD_TRAVEL_MS + STD_PLANT_MS;        // 320
const IMPACT_START = DASH_START + DASH_MS;                 // 600
const TOTAL        = IMPACT_START + IMPACT_MS;             // 900

export class JarvanDashVFX implements Animation {
  private elapsed = 0;
  private readonly firedHits = new Set<number>();
  onStart?: () => void;
  onEnd?: () => void;

  constructor(
    private visual: UnitVisual,
    private startPx: Point,
    private endPx: Point,
    private hexSize: number,
    // Pixel positions of enemies along the dash path, in encounter order
    private hitPositions: Point[] = [],
    private onHit: (index: number) => void = () => {},
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);

    // Slide Jarvan's visual from start to end during dash phase
    const dashT = Math.min(Math.max((this.elapsed - DASH_START) / DASH_MS, 0), 1);
    if (dashT > 0) {
      const eased = Easing.easeInOutCubic(dashT);
      this.visual.x = this.startPx.x + (this.endPx.x - this.startPx.x) * eased;
      this.visual.y = this.startPx.y + (this.endPx.y - this.startPx.y) * eased;
    }

    // Fire all hit callbacks the moment the dash completes (impact phase starts)
    if (this.elapsed >= IMPACT_START && this.firedHits.size === 0 && this.hitPositions.length > 0) {
      for (let i = 0; i < this.hitPositions.length; i++) {
        this.firedHits.add(i);
        this.onHit(i);
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed;

    // ── Phase 3: Lance trail (draw first so it sits behind the standard) ──
    if (t >= DASH_START && t < IMPACT_START) {
      this.drawLanceTrail(ctx, (t - DASH_START) / DASH_MS);
    }

    // ── Phase 4: Impact ring ──
    if (t >= IMPACT_START) {
      this.drawImpact(ctx, (t - IMPACT_START) / IMPACT_MS);
    }

    // ── Phase 1: Standard projectile flying ──
    if (t < STD_TRAVEL_MS) {
      this.drawStandardProjectile(ctx, t / STD_TRAVEL_MS);
    }

    // ── Phase 2 & 3: Standard planted (drawn on top) ──
    if (t >= STD_TRAVEL_MS && t < IMPACT_START) {
      const plantAge = t - STD_TRAVEL_MS;
      const growT = Easing.easeOutBack(Math.min(plantAge / 80, 1));
      const fadeStart = IMPACT_START - 120;
      const alpha = t > fadeStart ? 1 - (t - fadeStart) / 120 : 1;
      this.drawStandard(ctx, growT, Math.max(alpha, 0));
    }
  }

  private drawStandardProjectile(ctx: CanvasRenderingContext2D, t: number): void {
    const eased = Easing.easeOutQuad(t);
    const px = this.startPx.x + (this.endPx.x - this.startPx.x) * eased;
    const py = this.startPx.y + (this.endPx.y - this.startPx.y) * eased;
    const tailT = Easing.easeOutQuad(Math.max(0, t - 0.18));
    const tailX = this.startPx.x + (this.endPx.x - this.startPx.x) * tailT;
    const tailY = this.startPx.y + (this.endPx.y - this.startPx.y) * tailT;

    ctx.save();
    ctx.shadowColor = "#4090ff";
    ctx.shadowBlur = 18;

    // Trail line
    ctx.globalAlpha = 0.55;
    ctx.strokeStyle = "#1560bd";
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(px, py);
    ctx.stroke();

    // Orb head
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 24;
    ctx.fillStyle = "#4090ff";
    ctx.beginPath();
    ctx.arc(px, py, 5.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(px, py, 2.2, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawStandard(ctx: CanvasRenderingContext2D, growT: number, alpha: number): void {
    const h = this.hexSize * 1.6 * growT;
    if (h <= 0) return;

    ctx.save();
    ctx.translate(this.endPx.x, this.endPx.y);
    ctx.globalAlpha = alpha;

    // Lance pole (gold)
    ctx.strokeStyle = "#c8a020";
    ctx.lineWidth = 3;
    ctx.shadowColor = "#ffd040";
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.moveTo(0, this.hexSize * 0.4);
    ctx.lineTo(0, -h);
    ctx.stroke();

    // Demacian blue pennant (triangle pointing right)
    const flagTop = -h;
    const flagMid = -h + h * 0.45;
    const flagBase = -h + h * 0.6;
    ctx.fillStyle = "#1560bd";
    ctx.shadowColor = "#4090ff";
    ctx.shadowBlur = 14;
    ctx.beginPath();
    ctx.moveTo(0, flagTop);
    ctx.lineTo(this.hexSize * 0.72, flagMid);
    ctx.lineTo(0, flagBase);
    ctx.closePath();
    ctx.fill();

    // Gold tip spear
    ctx.fillStyle = "#ffd040";
    ctx.shadowColor = "#ffd040";
    ctx.shadowBlur = 22;
    ctx.beginPath();
    ctx.arc(0, -h, 3.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawLanceTrail(ctx: CanvasRenderingContext2D, dashT: number): void {
    // Current visual pos (already updated by update())
    const curX = this.visual.x;
    const curY = this.visual.y;
    const trailLen = Math.hypot(curX - this.startPx.x, curY - this.startPx.y);
    if (trailLen < 1) return;

    const angle = Math.atan2(this.endPx.y - this.startPx.y, this.endPx.x - this.startPx.x);
    const trailAlpha = 1 - dashT * 0.65;

    ctx.save();
    ctx.translate(this.startPx.x, this.startPx.y);
    ctx.rotate(angle);

    // Wide glow beam
    ctx.globalAlpha = trailAlpha * 0.35;
    ctx.fillStyle = "#1560bd";
    ctx.shadowColor = "#4090ff";
    ctx.shadowBlur = 28;
    ctx.fillRect(0, -9, trailLen, 18);

    // Bright core
    ctx.globalAlpha = trailAlpha * 0.9;
    ctx.fillStyle = "#ffffff";
    ctx.shadowBlur = 8;
    ctx.fillRect(0, -2.5, trailLen, 5);

    // Gold edge lines (Demacian lance detail)
    ctx.globalAlpha = trailAlpha * 0.6;
    ctx.strokeStyle = "#c8a020";
    ctx.lineWidth = 1.5;
    ctx.shadowColor = "#ffd040";
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.moveTo(0, -7);
    ctx.lineTo(trailLen, -7);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, 7);
    ctx.lineTo(trailLen, 7);
    ctx.stroke();

    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D, impT: number): void {
    const alpha = 1 - impT;
    const outerR = this.hexSize * 2.4 * Easing.easeOutCubic(impT);
    const innerR = outerR * 0.55;

    ctx.save();

    // Blue outer shockwave
    ctx.globalAlpha = alpha * 0.9;
    ctx.strokeStyle = "#1560bd";
    ctx.lineWidth = 5 * (1 - impT) + 1;
    ctx.shadowColor = "#4090ff";
    ctx.shadowBlur = 30;
    ctx.beginPath();
    ctx.arc(this.endPx.x, this.endPx.y, outerR, 0, Math.PI * 2);
    ctx.stroke();

    // Gold inner ring (Demacian accent)
    ctx.globalAlpha = alpha * 0.65;
    ctx.strokeStyle = "#c8a020";
    ctx.lineWidth = 2;
    ctx.shadowColor = "#ffd040";
    ctx.shadowBlur = 16;
    ctx.beginPath();
    ctx.arc(this.endPx.x, this.endPx.y, innerR, 0, Math.PI * 2);
    ctx.stroke();

    // White core flash
    const flashR = this.hexSize * 0.9 * (1 - Easing.easeInQuad(impT));
    if (flashR > 0) {
      ctx.globalAlpha = alpha * 0.85;
      ctx.fillStyle = "#ffffff";
      ctx.shadowBlur = 20;
      ctx.beginPath();
      ctx.arc(this.endPx.x, this.endPx.y, flashR, 0, Math.PI * 2);
      ctx.fill();
    }

    // Radial streaks
    ctx.shadowColor = "#4090ff";
    ctx.shadowBlur = 12;
    ctx.strokeStyle = "#1560bd";
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = alpha * 0.7;
    const streaks = 8;
    for (let i = 0; i < streaks; i++) {
      const a = (i / streaks) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(
        this.endPx.x + Math.cos(a) * innerR * 0.4,
        this.endPx.y + Math.sin(a) * innerR * 0.4,
      );
      ctx.lineTo(
        this.endPx.x + Math.cos(a) * outerR * 0.85,
        this.endPx.y + Math.sin(a) * outerR * 0.85,
      );
      ctx.stroke();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
